import { useState, useEffect, useContext } from 'react';
import styles from './Login.module.css';
import { useDispatch } from 'react-redux';
import toneLogo from '../../assests/images/tone-logo.png';
import image4 from '../../assests/images/Image 4.png';
import image5 from '../../assests/images/Image 5.png';
import { AuthResponse, LoginFormInputs } from '../../dtos/auth';
import { loginTrigger, getCSRFToken, addUserSessionTrigger } from '../../slices/authSlice';
import { AppDispatch } from '../../store/index';
import Cookies from 'universal-cookie';
import { useNavigate, NavLink } from 'react-router-dom';
import { getAuthDetails } from '../../utils/helper';
import { useToast, createToastHelpers } from '../Layout/Toast';
import { UserContext } from '../../contexts/UserContext';
import { v4 as uuidv4 } from 'uuid';
import Form from '../Layout/Form';

const LoginForm = () => {
  const cookies = new Cookies(null, { path: '/' });
  const isAuthorized = getAuthDetails();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [rememberMe, setRememberMe] = useState(false);
  const { setUser } = useContext(UserContext);
  const { showToast } = useToast();
  const toastHelpers = createToastHelpers(showToast);
  
  const [formData, setFormData] = useState<LoginFormInputs>({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error for this field
    if (formErrors[fieldName]) {
      setFormErrors(prev => {
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem('email');
    const savedPassword = localStorage.getItem('password');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedRememberMe && savedEmail && savedPassword) {
      setFormData({
        email: savedEmail,
        password: savedPassword
      });
      setRememberMe(savedRememberMe);
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      navigate('/home');
    }
  }, [isAuthorized, navigate]);
  const onSubmit = async (data: Record<string, any>) => {
    // Validate form data
    const errors: Record<string, string> = {};
    if (!data.email || data.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Invalid email address';
    }
    if (!data.password || data.password.trim() === '') {
      errors.password = 'Password is required';
    } else if (data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    const toastId = toastHelpers.loading('Processing...');
    
    try {
      const response = await dispatch(loginTrigger(data as LoginFormInputs)).unwrap();

      const responseData = response.data ? response.data : null;

      if (responseData) {
        cookies.set('x-session-id', uuidv4());
        toastHelpers.success(response.message);

        // Store credentials if "Remember Me" is checked
        if (rememberMe) {
          localStorage.setItem('email', data.email);
          localStorage.setItem('password', data.password);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('email');
          localStorage.removeItem('password');
          localStorage.removeItem('rememberMe');
        }

        if (response.loginType === 'otp') {
          dispatch(getCSRFToken());
          setUser(responseData);
          cookies.set('otp', true);
          setTimeout(() => {
            navigate(`/otp/${responseData.accessToken}`);
          }, 3000);
        } else {
          const userDetailsData = { accessToken: responseData.accessToken, user: responseData.user };
          cookies.set('userDetails', JSON.stringify(userDetailsData), { path: '/', secure: false, sameSite: 'lax' });
          dispatch(getCSRFToken())
            .unwrap()
            .then(() => {
              dispatch(addUserSessionTrigger());
              setUser(responseData);
              navigate('/home');
            })
            .catch((error) => {
              console.error('CSRF Token Error:', error);
              navigate('/home');
            });
        }
      } else {
        toastHelpers.error(response.message);
      }
    } catch (error) {
      toastHelpers.error('An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prevState: boolean) => !prevState);
  };

  // Form schema
  const formSchema = [
    {
      name: 'email',
      type: 'email' as const,
      label: 'Email',
      required: true,
      placeholder: 'Enter your email'
    },
    {
      name: 'password',
      type: 'password' as const,
      label: 'Password',
      required: true,
      placeholder: 'Enter your password',
      minLength: 6
    }
  ];
  return (
    <div className={styles.loginPage}>
      <div className={styles.leftBackground}>
        <img src={image4} alt="image54" />
      </div>
      <div className={styles.rightBackground}>
        <img src={image5} alt="image5" />
      </div>
      <div className={styles.loginWrapper}>
        <img src={toneLogo} alt="Logo" className={styles.brandLogo} />
        <div className={styles.loginFormContainer}>
          <h3 className={styles.welcomeText}>Welcome back </h3>
          
          <Form
            schema={formSchema}
            initialData={formData}
            onSubmit={onSubmit}
            onFieldChange={handleFormFieldChange}
            validationErrors={formErrors}
            isLoading={isSubmitting}
            submitButtonText="Sign in"
            submitButtonProps={{
              className: styles.submitButton
            }}
            className={styles.loginForm}
          />
          
          <div className={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={() => setRememberMe((prevState) => !prevState)}
              className={styles.checkboxInput}
            />
            <label htmlFor="rememberMe" className={styles.checkboxLabel}>
              Keep me logged in
            </label>
            <NavLink
              to="/forgot-password"
              className={styles.forgotPasswordLink}
            >
              Forgot password?
            </NavLink>
          </div>
          
          <hr />
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
